import type { TypeaheadResult } from '@luxuryescapes/contract-search'
import { queryNullChildren, ANYWHERE_PLACE_ID } from 'constants/search'
import {
  mapFlexibleNightsToDurationRange,
  convertDateRangesToMonthString,
  encodeChildrenOccupancy,
} from 'lib/search/searchUtils'

function getAirportCode(str: string): string | undefined {
  const openParenIndex = str.indexOf('(')
  if (openParenIndex === -1) return undefined
  return str.substring(openParenIndex + 1, openParenIndex + 4)
}

/**
 * Maps contract TypeaheadResult to App.SearchItem
 */
export function mapTypeaheadResultToSearchItem(result: TypeaheadResult): App.SearchItem {
  const baseItem = {
    searchType: result.searchType,
    value: result.id,
    format: {
      mainText: result.format.mainText ?? result.name ?? '',
      secondaryText: result.format.secondaryText ?? undefined,
      imageId: result.format.imageId,
    },
  }

  // Handle discriminated union properly based on searchType
  switch (result.searchType) {
    case 'destination':
      return {
        ...baseItem,
        searchType: 'destination' as const,
        destinationType: result.destinationType,
      }

    case 'property':
      return {
        ...baseItem,
        searchType: 'property' as const,
        offerId: result.offerId,
      }

    case 'airport':
      return {
        ...baseItem,
        searchType: 'airport' as const,
        airportCode: result.format.mainText
          ? getAirportCode(result.format.mainText) ?? ''
          : '',
      }

    case 'landmark':
      return {
        ...baseItem,
        searchType: 'landmark' as const,
      }

    case 'lat_long':
      return {
        ...baseItem,
        searchType: 'lat_long' as const,
      }

    case 'city':
    case 'address':
    case 'province':
    case 'state':
      // Map city/address/province/state to destination
      return {
        ...baseItem,
        searchType: 'destination' as const,
        destinationType: result.destinationType,
      }

    case 'map_area':
      return {
        ...baseItem,
        searchType: 'bounds' as const,
      }

    case 'train_station':
      // Map train_station to landmark
      return {
        ...baseItem,
        searchType: 'landmark' as const,
      }

    case 'naturalLanguage':
      return {
        ...baseItem,
        searchType: 'naturalLanguage' as const,
      }

    case 'experience':
      return {
        ...baseItem,
        searchType: 'experience' as const,
      }

    case 'tour':
      return {
        ...baseItem,
        searchType: 'tour' as const,
      }

    default:
      // Fallback for any unhandled types - map to destination as safe default
      return {
        ...baseItem,
        searchType: 'destination' as const,
      }
  }
}

/**
 * Maps extracted typeahead streaming data to NaturalLanguageExtractedSearchParams
 * @param streamingData The streaming data containing extracted information
 * @param query The natural language query string to use for name fields
 * @returns Extracted search parameters object
 */
export function mapStreamingDataToExtractedParams(
  streamingData: App.TypeaheadStreamingData | undefined,
  query: string | undefined,
): App.NaturalLanguageExtractedSearchParams | undefined {
  if (!streamingData) {
    return undefined
  }

  const extractedParams: App.NaturalLanguageExtractedSearchParams = {}

  // Extract location
  if (streamingData.location?.data) {
    const location = streamingData.location.data
    if ('placeIds' in location && location.placeIds.length > 0) {
      if (location.searchType !== 'landmark') {
        // Support multiple destination IDs by joining with commas
        extractedParams.destinationId = location.placeIds.join(',')
      } else if (location.searchType === 'landmark') {
        // Support multiple landmark IDs by joining with commas
        extractedParams.landmarkId = location.placeIds.join(',')
      }
    } else if (location.searchType === 'lat_long') {
      extractedParams.lat = location.lat
      extractedParams.lng = location.lng
    }
  }

  // Extract property ID (if separate from location)
  if (streamingData.propertyId?.data) {
    // Server expects property ID to be prefixed with "le:" or "bedbank:"
    extractedParams.propertyId = `le:${streamingData.propertyId.data}`
  }

  // Set default location (Anywhere) only if no location AND no property was extracted
  const hasLocation = !!(extractedParams.destinationId || extractedParams.landmarkId || extractedParams.lat)
  const hasProperty = !!extractedParams.propertyId

  // Only set "Anywhere" if there's no location AND no property
  if (!hasLocation && !hasProperty) {
    extractedParams.destinationId = ANYWHERE_PLACE_ID
  }

  // Set query parameter if natural language query exists
  if (query) {
    extractedParams.query = query
  }

  // Extract travel dates
  if (streamingData.travelDates?.data) {
    const travelDates = streamingData.travelDates.data
    if (travelDates.type === 'specific') {
      extractedParams.checkIn = travelDates.checkIn
      extractedParams.checkOut = travelDates.checkOut
    } else if (travelDates.type === 'flexible') {
      const flexibleNights = mapFlexibleNightsToDurationRange(travelDates.numberOfNights)
      const flexibleMonths = convertDateRangesToMonthString(travelDates.dateRanges)
      if (flexibleNights && flexibleMonths) {
        extractedParams.flexibleNights = flexibleNights
        extractedParams.flexibleMonths = flexibleMonths
      }
    }
  }

  // Extract occupancy
  if (streamingData.occupancy?.data) {
    const occupancy = streamingData.occupancy.data
    extractedParams.adults = occupancy.adults.toString()
    extractedParams.children = encodeChildrenOccupancy(occupancy.childrenAges)
  } else {
    // Set default occupancy if no occupancy was extracted (2 adults, no children)
    extractedParams.adults = '2'
    extractedParams.children = queryNullChildren
  }

  // Extract inclusions
  if (streamingData.inclusions?.data && streamingData.inclusions.data.length > 0) {
    extractedParams.inclusions = streamingData.inclusions.data
  }

  // Extract amenities
  if (streamingData.amenities?.data && streamingData.amenities.data.length > 0) {
    extractedParams.amenities = streamingData.amenities.data
  }

  // Extract tags
  if (streamingData.tags?.data && streamingData.tags.data.length > 0) {
    extractedParams.tags = streamingData.tags.data
  }

  // Extract sortBy
  if (streamingData.sortBy?.data === 'value') {
    extractedParams.sortBy = 'value'
  }

  // Extract price filters
  if (streamingData.price?.data) {
    const price = streamingData.price.data
    if (price.priceGte !== undefined) {
      extractedParams.priceGte = price.priceGte.toString()
    }
    if (price.priceLte !== undefined) {
      extractedParams.priceLte = price.priceLte.toString()
    }
  }

  // Return undefined if no params were extracted
  if (Object.keys(extractedParams).length === 0) {
    return undefined
  }

  return extractedParams
}
